import { Box, Button, Drawer, Typography, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import ClearFiltersButton from "./ClearFiltersButton";
import CustomCheckboxLabel from "../CustomCheckboxLabel";

const MobileFilters = ({
  selectedCategories,
  setSelectedCategories,
  selectedLocations,
  setSelectedLocations,
  categories = [],
  zones = [],
  selectedMemberRanges = [],
  setSelectedMemberRanges,
  memberRanges = [],
  clearFilters,
  hasFilters,
}) => {
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

  const { t } = useTranslation(["header-filters", "categories", "districts"]);

  const totalSelectedFilters =
    (selectedCategories?.length || 0) +
    (selectedLocations?.length || 0) +
    (selectedMemberRanges?.length || 0);

  const toggleCategory = (id) => {
    if (selectedCategories.includes(id)) {
      setSelectedCategories(selectedCategories.filter((c) => c !== id));
    } else {
      setSelectedCategories([...selectedCategories, id]);
    }
  };

  const toggleLocation = (id) => {
    if (selectedLocations.includes(id)) {
      setSelectedLocations(selectedLocations.filter((z) => z !== id));
    } else {
      setSelectedLocations([...selectedLocations, id]);
    }
  };

  const toggleMemberRange = (value) => {
    if (selectedMemberRanges.includes(value)) {
      setSelectedMemberRanges(selectedMemberRanges.filter((r) => r !== value));
    } else {
      setSelectedMemberRanges([...selectedMemberRanges, value]);
    }
  };

  const handleClose = () => {
    setIsMobileFilterOpen(false);
  };

  return (
    <>
      <Box display={{ xs: "flex", sm: "none" }} mr={"8px"}>
        <Button
          onClick={() => setIsMobileFilterOpen(true)}
          variant="outlined"
          sx={{
            position: "relative",
            textTransform: "none",
            borderRadius: "8px",
            px: "12px",
            py: "8px",
            height: 39,
            fontSize: "15px",
            color: "rgba(62, 63, 76, 1)",
            backgroundColor: "#fff",
            borderColor: "rgba(234, 234, 234, 1)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <img
                src="/icons/filter-icon.svg"
                alt="icon"
                width={18}
                height={18}
              />
              <Typography
                sx={{
                  fontSize: "15px",
                  fontWeight: 400,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  textAlign: "left",
                }}
              >
                {t("mobile_filters.filter_by")}
              </Typography>
            </Box>
            <img src="/icons/arrow-down.svg" alt="arrow-down" />
          </Box>
          {totalSelectedFilters > 0 && (
            <Box
              sx={{
                position: "absolute",
                top: -8,
                right: -8,
                backgroundColor: "rgba(70, 160, 145, 1)",
                color: "#fff",
                borderRadius: "50%",
                minWidth: 21,
                minHeight: 21,
                aspectRatio: 1,
                fontSize: 15,
                fontWeight: 400,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {totalSelectedFilters}
            </Box>
          )}
        </Button>
      </Box>

      <Drawer
        anchor="bottom"
        open={isMobileFilterOpen}
        onClose={() => setIsMobileFilterOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "16px 16px 0 0",
            p: "24px 16px 24px",
            minHeight: "300px",
            maxHeight: "90vh",
          },
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap="6px"
          mb={"32px"}
        >
          <Box display="flex" alignItems="center" gap="6px">
            <img
              src="/icons/filter-icon.svg"
              alt="filter"
              width={18}
              height={18}
            />
            <Typography
              fontWeight={500}
              fontSize={15}
              color="rgba(62, 63, 76, 1)"
            >
              {t("mobile_filters.filter_by")}
            </Typography>
          </Box>

          <ClearFiltersButton
            hasFilters={hasFilters}
            clearFilters={clearFilters}
            isMobile={true}
          />
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "32px" }}>
          {/* Categories */}
          {categories.length > 0 ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <Box display="flex" alignItems="center" gap="6px">
                <img
                  src="/icons/grid-view-icon.svg"
                  alt="category"
                  width={18}
                  height={18}
                />
                <Typography
                  fontWeight={500}
                  fontSize={15}
                  color="rgba(62, 63, 76, 1)"
                >
                  {t("mobile_filters.categories")}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {categories.map((category) =>
                  category.sub_categories.map((subCategory) => {
                    const isSelected = selectedCategories.includes(
                      subCategory.id
                    );
                    return (
                      <Box
                        key={subCategory.id}
                        onClick={() => toggleCategory(subCategory.id)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          p: "8px",
                          cursor: "pointer",
                          borderRadius: "8px",
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                          },
                        }}
                      >
                        <CustomCheckboxLabel
                          checked={isSelected}
                          onChange={() => toggleCategory(subCategory.id)}
                        />
                        <Typography
                          fontWeight={400}
                          fontSize={14}
                          color="rgba(47, 43, 61, 0.9)"
                        >
                          {t(subCategory.name, { ns: "categories" })}
                        </Typography>
                      </Box>
                    );
                  })
                )}
              </Box>
            </Box>
          ) : null}

          {/* Locations */}
          {zones.length > 0 ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <Box display="flex" alignItems="center" gap="6px">
                <img
                  src="/icons/grid-view-icon.svg"
                  alt="category"
                  width={18}
                  height={18}
                />
                <Typography
                  fontWeight={500}
                  fontSize={15}
                  color="rgba(62, 63, 76, 1)"
                >
                  {t("mobile_filters.location")}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {zones.map((zone) => {
                  const isSelected = selectedLocations.includes(zone.id);
                  return (
                    <Box
                      key={zone.id}
                      onClick={() => toggleLocation(zone.id)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        p: "8px",
                        cursor: "pointer",
                        borderRadius: "8px",
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.04)",
                        },
                      }}
                    >
                      <CustomCheckboxLabel
                        checked={isSelected}
                        onChange={() => toggleLocation(zone.id)}
                      />
                      <Typography
                        fontWeight={400}
                        fontSize={14}
                        color="rgba(47, 43, 61, 0.9)"
                      >
                        {t(zone.name, { ns: "districts" })}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ) : null}

          {/* Member Ranges */}
          {memberRanges.length > 0 ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <Box display="flex" alignItems="center" gap="6px">
                <img
                  src="/icons/grid-view-icon.svg"
                  alt="category"
                  width={18}
                  height={18}
                />
                <Typography
                  fontWeight={500}
                  fontSize={15}
                  color="rgba(62, 63, 76, 1)"
                >
                  {t("mobile_filters.member_range")}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {memberRanges.map((range) => {
                  const isSelected = selectedMemberRanges.includes(range.value);
                  return (
                    <Box
                      key={range.value}
                      onClick={() => toggleMemberRange(range.value)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        p: "8px",
                        cursor: "pointer",
                        borderRadius: "8px",
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.04)",
                        },
                      }}
                    >
                      <CustomCheckboxLabel
                        checked={isSelected}
                        onChange={() => toggleMemberRange(range.value)}
                      />
                      <Typography
                        fontWeight={400}
                        fontSize={14}
                        color="rgba(47, 43, 61, 0.9)"
                      >
                        {range.key}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ) : null}
        </Box>

        <Box
          sx={{
            position: "fixed",
            top: "10px",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1300,
          }}
        >
          <IconButton onClick={handleClose} sx={{ zIndex: 1300 }}>
            <CloseIcon
              sx={{
                color: "rgba(255, 255, 255, 1)",
                fontSize: "28px",
              }}
            />
          </IconButton>
        </Box>
      </Drawer>
    </>
  );
};

export default MobileFilters;
